import React from 'react';

import { Callout, Step } from './shared';

const Wizard: React.FC = () => (
  <div>
    <h2 className="text-2xl font-bold mb-4">Spec-to-Project Wizard</h2>
    <p className="text-sm text-base-content/70 mb-5 leading-relaxed">
      Starting something new doesn&apos;t require syncing an existing project first. Describe what you&apos;re building
      in plain language — a sentence is enough — and MFlow proposes a methodology and task breakdown you review before
      anything is created.
    </p>

    <div className="space-y-3 mb-6">
      <Step n={1}>
        Click <strong>New project</strong> at the top of the sidebar. This works from any page, any time — you
        don&apos;t need to be mid-onboarding to use it.
      </Step>
      <Step n={2}>
        Describe your project, optionally attaching a spec document (
        <code className="bg-base-200 px-1 rounded text-xs">.pdf</code>,{' '}
        <code className="bg-base-200 px-1 rounded text-xs">.doc</code>,{' '}
        <code className="bg-base-200 px-1 rounded text-xs">.docx</code>).
      </Step>
      <Step n={3}>
        Review the proposed methodology and task list. Reply in chat to refine it — add, remove, or reword tasks —
        before building anything.
      </Step>
      <Step n={4}>
        Pick where to build it: <strong>Jira</strong>, <strong>Trello</strong>, or MFlow&apos;s own{' '}
        <strong>Standalone</strong> board (no setup — recommended if you don&apos;t already use Jira or Trello).
      </Step>
      <Step n={5}>
        Watch each task get created with live progress. If a task fails to create, retry just the failed ones without
        rebuilding the whole project.
      </Step>
    </div>

    <Callout type="tip">
      The same Wizard also appears during first-time onboarding — &quot;Start a new project from a spec&quot; is the
      alternative to syncing an existing Jira or Trello workspace.
    </Callout>

    <Callout type="info">
      Building onto <strong>Jira</strong> preserves epic/subtask hierarchy from the proposal. On Trello and Standalone
      every task is still created, just flattened — there&apos;s no subtask concept to build it into there.
    </Callout>

    <p className="text-sm text-base-content/70 mt-4 leading-relaxed">
      Once the project is built, the Wizard&apos;s job is done — a small <strong>Agent</strong> toggle appears in the
      chat letting you switch from <strong>Wizard</strong> to the platform&apos;s own agent (e.g. Jira) for that
      project, so you can keep managing it in the same conversation going forward.
    </p>
  </div>
);

export default Wizard;
